import { useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';
import { clearScopedTenantId } from 'services/api/config/scopedTenant';
import { useCognito } from 'common/hooks';
import { usePostLogin } from 'services/hooks/mutations';
import { COGNITO_PASSWORD_HINT, isValidCognitoPassword } from 'common/constants';
import { LoginFormData } from '../Login.types';

export function useLoginFlow() {
    const navigate = useNavigate();
    const { authenticateUser, completeNewPasswordChallenge } = useCognito();
    const { postLogin } = usePostLogin();

    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);
    const [needsNewPassword, setNeedsNewPassword] = useState(false);
    // Cognito NEW_PASSWORD challenge finished; Platform /auth verify still pending.
    const [passwordChallengeDone, setPasswordChallengeDone] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    // Email captured at challenge start (credentials form owns its own useForm).
    const [challengeEmail, setChallengeEmail] = useState('');

    const pendingChallengeRef = useRef<{
        cognitoUser: AmazonCognitoIdentity.CognitoUser;
        userAttributes: Record<string, string>;
        requiredAttributes: string[];
    } | null>(null);
    // Survives a failed postLogin after Cognito already accepted the new password —
    // retry must not re-run completeNewPasswordChallenge (temp password is gone).
    const completedIdTokenRef = useRef<string | null>(null);

    const finishLogin = (idToken: string) => {
        clearScopedTenantId();
        postLogin(idToken, {
            onSuccess: ({ userRole }) => {
                completedIdTokenRef.current = null;
                localStorage.setItem('userRole', JSON.stringify(userRole ?? []));
                navigate('/dashboard');
            },
            onError: (loginError) => {
                // Keep completedIdTokenRef so the user can retry Platform login
                // without re-entering a Cognito challenge that is already done.
                console.error('Login failed:', loginError);
                setError(loginError.message || 'Login failed');
                setIsLoading(false);
            },
        });
    };

    const handleLogin = async (data: LoginFormData) => {
        setError(null);
        setIsLoading(true);

        const email = data.email.trim().toLowerCase();
        try {
            const cognitoUser = authenticateUser(email, data.password, {
                onSuccess: function (result) {
                    pendingChallengeRef.current = null;
                    finishLogin(result.getIdToken().getJwtToken());
                },

                onFailure: function (err) {
                    console.error('Cognito authentication error:', err);
                    pendingChallengeRef.current = null;
                    setError(err.message || 'Authentication failed');
                    setIsLoading(false);
                },

                newPasswordRequired: function (userAttributes, requiredAttributes) {
                    pendingChallengeRef.current = {
                        cognitoUser,
                        userAttributes:
                            userAttributes && typeof userAttributes === 'object'
                                ? (userAttributes as Record<string, string>)
                                : {},
                        requiredAttributes: Array.isArray(requiredAttributes) ? (requiredAttributes as string[]) : [],
                    };
                    completedIdTokenRef.current = null;
                    setChallengeEmail(email);
                    setPasswordChallengeDone(false);
                    setNeedsNewPassword(true);
                    setNewPassword('');
                    setConfirmPassword('');
                    setError(null);
                    setIsLoading(false);
                },
            });
        } catch (err) {
            console.error('Login error:', err);
            setError((err as { message: string })?.message || 'Login failed');
            setIsLoading(false);
        }
    };

    const handleNewPasswordSubmit = (e: FormEvent) => {
        e.preventDefault();
        setError(null);

        // Cognito already succeeded earlier; only retry Platform cookie/session.
        if (completedIdTokenRef.current) {
            setIsLoading(true);
            finishLogin(completedIdTokenRef.current);
            return;
        }

        const pending = pendingChallengeRef.current;
        if (!pending) {
            setNeedsNewPassword(false);
            setPasswordChallengeDone(false);
            setError(
                'Sign-in session expired. Sign in again with the password you just set (not the temporary invite password).'
            );
            return;
        }
        if (!isValidCognitoPassword(newPassword)) {
            setError(COGNITO_PASSWORD_HINT);
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsLoading(true);
        completeNewPasswordChallenge(
            pending.cognitoUser,
            newPassword,
            pending.userAttributes,
            {
                onSuccess: (result) => {
                    // Challenge is consumed; keep the ID token so a postLogin failure
                    // can retry without asking for the (now invalid) temporary password.
                    const idToken = result.getIdToken().getJwtToken();
                    completedIdTokenRef.current = idToken;
                    pendingChallengeRef.current = null;
                    setPasswordChallengeDone(true);
                    finishLogin(idToken);
                },
                onFailure: (err) => {
                    console.error('Complete new password error:', err);
                    setError(err.message || 'Failed to set new password');
                    setIsLoading(false);
                },
            },
            pending.requiredAttributes
        );
    };

    const handleBackToLogin = () => {
        pendingChallengeRef.current = null;
        completedIdTokenRef.current = null;
        setNeedsNewPassword(false);
        setPasswordChallengeDone(false);
        setNewPassword('');
        setConfirmPassword('');
        setChallengeEmail('');
        setError(null);
    };

    return {
        error,
        setError,
        isLoading,
        setIsLoading,
        isForgotPasswordModalOpen,
        setIsForgotPasswordModalOpen,
        needsNewPassword,
        passwordChallengeDone,
        newPassword,
        setNewPassword,
        confirmPassword,
        setConfirmPassword,
        challengeEmail,
        navigate,
        handleLogin,
        handleNewPasswordSubmit,
        handleBackToLogin,
    };
}
